import { ReactElement, useEffect, useState } from "react";
import FilterComponentBase from "../../../components/filterComponentBase";
import { useFetch } from "../../../system/useFetch";
import {
  IContentAreaProps,
  IDictionaryNumericKey,
  IProduction, IShiftHistory,
  IState,
  IStrandProduction
} from "../../../types";
import { useSelector } from "react-redux";
import { Alert, Collapse, IconButton, Paper } from "@mui/material";
import ProductionHeader from "./productionHeader";
import ProductionChart from "./productionChart";
import { IAuxiliaryData, IMillProductDetail } from "../types";
import { ProductionReportContext } from "./productionReportContext";
import {
  IAxisBand,
  IAxisLinear,
  IPoint as CommonPoint,
  IRadarChartConfig
} from "../../../components/charts/types";
import { getCurrentDatetimeInSeconds } from "../../../system/utils";
import { getPalette } from "../../../configuration/configuration";
import RadarContainer from "./radarContainer";
import ProductionTabPanel from "./productionTabPanel";
import SkeletonView from "../../../components/skeletonView";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";


const contentStyle = {
  gridTemplateColumns: "1fr",
  gridTemplateRows: "auto 1fr",
  gridRow: "2 / span 1",
  overflow: "hidden",
  gap: "8px"
};

const headerStyle = {
  gridColumn: "1 / 1",
  gridRow: "1 / span 1"
};

const radialStyle = {
  marginLeft: 50,
  gridColumn: "2 / 1 span",
  gridRow: "1 / span 2",
  width: 280,
  height: 280,
};

const ganttStyle = {
  gridColumn: "1 / span 1",
  gridRow: "2 / span 1"
};

// const tabStyle = {
//   // padding: "1em",
//   gridColumn: "1 / span 2",
//   gridRow: "3 / span 1",
//   overflow: "hidden"
// };

const secondsByCoil = 3600 / 60.0;
const pollInterval = 300000;  // 5 min
const collapseChartsHeight = 32;
const expandedChartsHeight = 315;

const ProductionReportRM = ({ parentSize }: IContentAreaProps): ReactElement => {
  const [open, setOpen] = useState<boolean>(false);
  const filter = useSelector((state: IState) => state.filter);
  const [ radarChartConfig, setRadarChartConfig ] = useState<IRadarChartConfig | null>(null);
  const [auxiliaryData, setAuxiliaryData] = useState<IDictionaryNumericKey<IAuxiliaryData> | null>(null);
  const { loading, error, data: productionData } = useFetch<[IProduction[], IMillProductDetail[], IShiftHistory[]]>(
    [{
      module: "production",
      query: "getProductionGanttByFilter",
      payload: { ...filter.remote }
    },
    {
      module: "mill",
      query: "getMillProductionDetailByFilter",
      payload: { ...filter.remote }
    },
    {
      module: "production",
      query: "getShiftHistory",
      payload: { ...filter.remote }
    }], filter.local.mode === "REAL_TIME" ? pollInterval : 0);


  useEffect(() => {
    if (!productionData) {
      setAuxiliaryData(null);

      return;
    }

    const auxiliary: IDictionaryNumericKey<IAuxiliaryData> = {};
    const auxiliaryByMachine: IDictionaryNumericKey<IAuxiliaryData> = {};
    const items: CommonPoint[] = [];
    let maximumY = 0;

    productionData[1].forEach(billet => {

      let aux = auxiliaryByMachine[billet.productionMachineId];
      if(!aux) {
        aux = {
          Id: billet.productionMachineId,
          billets: 0,
          weight: 0,
          factor: 0,
          pendingWeight: false,
          std: "BAD",
          machineData: []
        };

        auxiliaryByMachine[billet.productionMachineId] = aux;
      }

      aux.billets += 1;
      aux.weight += billet.weight ?? 0;
      aux.pendingWeight = aux.pendingWeight || billet.weight === null;
    });


    productionData[0].forEach(order => {

      if(!auxiliary[order.productionID]) {
        auxiliary[order.productionID] = {
          Id: order.productionID,
          billets: 0,
          weight: 0,
          factor: 0,
          pendingWeight: false,
          std: "BAD",
          machineData: []
        };
      }

      let totalBilletsByOrder = 0;

      order.productionMachines.forEach((productionMachine) => {
        const aux = auxiliaryByMachine[productionMachine.productionMachineID];
        if(aux) {
          auxiliary[order.productionID].billets += aux.billets;
          auxiliary[order.productionID].weight += aux.weight;
          auxiliary[order.productionID].pendingWeight = auxiliary[order.productionID].pendingWeight || aux.pendingWeight;
          auxiliary[order.productionID].machineData.push(aux);
          totalBilletsByOrder += aux.billets;
        }
      });

      const timeDiff = (order.endDatetime ?? getCurrentDatetimeInSeconds()) - order.beginDatetime;
      const productionTime = timeDiff / (totalBilletsByOrder ?? 1);
      auxiliary[order.productionID].factor = 1 - Math.min(1, secondsByCoil / productionTime);
      maximumY = Math.max(maximumY, auxiliary[order.productionID].factor);

      const factorEf = secondsByCoil/ productionTime;

      auxiliary[order.productionID].std = factorEf >= .9 ? "GOOD" : factorEf > .8 ? "WARNING" : "BAD";

      items.push({
        x: (order.productionMachines[0] as IStrandProduction).millHeat,
        y: auxiliary[order.productionID].factor
      });

    });

    const yAxis: IAxisLinear = {
      name: "s",
      minimum: 0,
      maximum: maximumY,
      ticksCount: 4,
      formatTick: (n) => (n as number * secondsByCoil).toFixed(0)
    };

    const xAxis: IAxisBand = {
      name: "millHeat",
      categories: productionData[0].map(o => (o.productionMachines[0] as IStrandProduction).millHeat)
    };

    setRadarChartConfig({
      width: 250,
      height: 250,
      title: "Gap Time",
      showLegend: true,
      series: [{
        name: "Mill Orders",
        xAxisName: "millHeat",
        yAxisName: "s",
        value: items,
        color: getPalette().list[5],
        unit: "s",
        ranges: [
          {
            minimum: 0,
            maximum: .1,
            color: getPalette().statusColor.good.background },
          {
            minimum: .1,
            maximum: .2,
            color: getPalette().statusColor.warning.background },
          {
            minimum: .2,
            maximum: 1,
            color: getPalette().statusColor.error.background }]
      }],
      yAxis,
      xAxis
    });

    setAuxiliaryData(auxiliary);

  }, [productionData]);

  const hasData = productionData && productionData[0].length > 0;

  const gridStyle = {
    ...parentSize,
    gridTemplateRows: "auto 1fr",
    gridTemplateColumns: "1fr"
  };

  return (
    <ProductionReportContext>
      <div className="display-grid" style={gridStyle}>
        <FilterComponentBase loading={loading}/>

        {loading && <SkeletonView parentSize={parentSize}/>}
        {error && <Alert severity="warning">{error.message}</Alert>}
        {!loading && !error && !hasData && <Alert severity="warning">No Data to Display.</Alert>}

        {!loading && !error && hasData && productionData &&
          <div className="display-grid" style={contentStyle}>
            <Paper style={{ height: open ? expandedChartsHeight : collapseChartsHeight }}>
              <div className="display-flex-row mb align-items-center" style={{ padding: ".25em .5em" }}>
                <IconButton onClick={() => setOpen(!open)} size={"small"} className="mr-1">
                  {open
                    ? <ExpandLessIcon fontSize="inherit" fontWeight="bold"/>
                    : <ExpandMoreIcon fontSize="inherit" fontWeight="bold"/>
                  }
                </IconButton>

                {!open && <strong>Gantt</strong>}
              </div>
              <Collapse in={open}>
                <div className="display-grid" style={{ width: parentSize.width, display: open ? undefined : "none" }}>
                  <div style={headerStyle}>
                    <ProductionHeader productionData={productionData[0]} auxiliaryData={auxiliaryData}/>
                  </div>
                  <div className="align-items-center justify-content-center" style={radialStyle}>
                    {radarChartConfig && <RadarContainer radarConfig={radarChartConfig}/>}
                  </div>
                  <div style={ganttStyle}>
                    <ProductionChart productionData={productionData[0]} productDetail={productionData[1]}
                                     shiftHistory={productionData[2]}/>
                  </div>
                </div>
              </Collapse>
            </Paper>

            <ProductionTabPanel productionData={productionData[0]} productDetail={productionData[1]}
                                  parentSize={{  ...parentSize, height: parentSize.height - (open ? expandedChartsHeight : collapseChartsHeight ) - 70 }} auxiliaryData={auxiliaryData}/>
          </div>
        }
      </div>
    </ProductionReportContext>
  )
  ;
};

export default ProductionReportRM;
